<template>
    <div>
        <div v-if="showCheckout" class="tw-checkout tw-stage tw-responsive__xs--half-padding tw-responsive__xxs--half-padding tw-text--center">
            <p class="tw-text--lg">
                Hang in there, we're just processing your order. Tech tock, tech tock.
                <br>DesignMyNight.com
            </p>

            <loading-splash></loading-splash>

            <p v-if="checkoutMode === 'modal'">
                Loading checkout pop up.
            </p>

            <p v-if="checkoutMode === 'blank'">
                Loading checkout in new window.
            </p>

            <div v-if="showSafariFallbackMessage" class="tw-safari-fallback tw-margin--top">
                <p class="tw-text--sm">
                    You are being redirected to our secure checkout. If this does not happen automatically, 
                    <a :href="safariCheckoutUrl" target="_blank" class="tw-link">click here</a>.
                </p>
            </div>
        </div>
        <checkout-v2-iframe 
            v-if="showCheckoutV2Iframe" 
            :params="checkoutV2Params"
            @close-checkout-iframe="closeCheckoutV2Iframe">
        </checkout-v2-iframe>
    </div>
</template>

<script lang='ts'>
    import { Component } from "vue-property-decorator";
    import TicketWidgetComponent from '../TicketWidgetComponent';
    import CheckoutV2Iframe from '../checkout/CheckoutV2Iframe.vue';
    import LoadingSplash from '../misc/loading-splash/LoadingSplash.vue';

    declare global {
        interface Window {
            DMN:any;
        }

        interface CheckoutV2Params {
            src: string;
            title: string;
            class?: string|undefined;
            allow?: string|undefined;
            allowpaymentrequest?: string|undefined;
        }
    }

    @Component({
        components: {
            LoadingSplash,
            CheckoutV2Iframe,
        }
    })

    export default class Checkout extends TicketWidgetComponent {
        showCheckout: boolean = false;
        showCheckoutV2Iframe: boolean = false;
        showSafariFallbackMessage: boolean = false;
        safariCheckoutUrl: string = '';
        checkoutV2Params: CheckoutV2Params = {
            src: '',
            allow: 'payment',
            allowpaymentrequest: 'true',
            title: 'Checkout',
            class: 'checkout-container-iframe',
        };

        checkoutConfig: any = {
            mode: this.dmnOptions.checkoutMode,
            url: undefined,
            modalContainerId: 'tonicTicketWidget_modal_checkout',
            ready: false
        };

        get checkoutMode() : string {
            return this.dmnOptions.checkoutMode;
        }

        mounted() {
            this.goToCheckout();
        }

        /**
         * Checks if checkout version 2 should be shown
         */
        isCheckoutV2() {
            return this.dmnOptions.checkoutVersion === '2';
        }

        /**
         * Resets Checkout V2 parameters to default values
         */
        resetCheckoutV2Params() {
            this.checkoutV2Params = {
                src: '',
                allow: 'payment',
                allowpaymentrequest: 'true',
                title: 'Checkout',
                class: 'checkout-container-iframe',
            };
        }

        /**
         * Closes Checkout V2 iframe
         */
        closeCheckoutV2Iframe() {
            this.showCheckoutV2Iframe = false;
            this.resetCheckoutV2Params();
            this.handleGoBack();
        }

        /**
         * @desc prepares the API request for sending to checkout
         * @return {Promise}
         */
        goToCheckout() {
            if(!this.cartManager.getTotalQuantitySelected()){
                return false;
            }

            let params = this.cartManager.sendToCheckout();
            this.showCheckout = true;

            return this.dmnResources.ticketWidget().sendToCheckout(params).then(
                (data:any) => {
                    // sometimes error messages come back in the purchase field
                    if(!data.purchase || !data.purchase.id) {
                        this.handleCheckoutError();
                        return;
                    }

                    this.cartManager.purchaseId = data.purchase.id;

                    if (this.isCheckoutV2()) {
                        this.openCheckoutV2();
                        return;
                    }

                    if (window.DMN && typeof window.DMN.goToCheckout === 'function') {
                        window.DMN.goToCheckout(data);
                        this.handleGoBack();
                        this.showCheckout = false;

                        return;
                    }

                    this.openCheckoutLink();
                },
                (error:any) => {
                    // if there is an error message then display it
                    if(error.data && error.data.error_code && error.data.message) {
                        this.$root.$data.checkoutErrorMessage = error.data.message;
                    }

                    this.handleCheckoutError() ;
                }
            );
        }

        /**
         * @param {string}
         */
        handleGoBack(stage: string = 'tickets') {

            // go back to the first stage
            stage = this.$root.$data.stages['calendar'] ? this.$root.$data.stages['calendar'].id : stage;
            stage = this.$root.$data.stages['events_list'] ? this.$root.$data.stages['events_list'].id : stage;

            this.showCheckout = false;
            this.showSafariFallbackMessage = false;
            this.safariCheckoutUrl = '';
            this.cartManager.resetCart();
            this.$router.push({path: stage});
        }

        handleCheckoutError() {
            this.showCheckoutError = true;

            this.handleGoBack('tickets');
        }

        openCheckoutV2() {
            const checkoutUrl = this.cartManager.getCheckoutUrl(true);
            if (!checkoutUrl) {
                return;
            }

            if (this.checkoutInitiatedFromMobile()) {
                window.open(checkoutUrl, '_blank');
                return;
            }

            this.checkoutV2Params.src = checkoutUrl;
            this.showCheckoutV2Iframe = true;
            this.showCheckout = false;
        }

        openCheckoutLink()
        {
            let checkoutUrl = this.cartManager.getCheckoutUrl();

            if (!checkoutUrl) {
                return;
            }

            this.checkoutConfig.url = checkoutUrl;
            this.checkoutConfig.ready = true;

            // Safari compatibility: Use window.open with parameters instead of form submission
            if (this.isSafariBrowser()) {
                this.openCheckoutLinkForSafari(checkoutUrl);
                return;
            }

            const id = 'tw_checkoutlink_' + Math.floor(Math.random() * 100);
            const body = window.document.getElementsByTagName('body');

            const form = window.document.createElement('form');
            form.setAttribute('action', checkoutUrl);
            form.setAttribute('method', 'GET');
            form.setAttribute('id', id);

            // when checkout mode is blank, open in new page
            if (this.dmnOptions.checkoutMode === 'blank') {
                form.setAttribute('target', '_blank');
            // when in iframe, target parent
            } else if (window.self !== window.top) {
                form.setAttribute('target', '_parent');
            }

            const checkoutParams = this.cartManager.getCheckoutParams();

            checkoutParams.forEach((param) => {
                const input = window.document.createElement('input');
                input.setAttribute('type', 'hidden');
                input.setAttribute('name', param.key);
                input.setAttribute('value', param.value);

                form.appendChild(input);
            })

            body[0].appendChild(form);

            // Try form submission with timeout fallback for Safari
            try {
                form.submit();
                
                // Fallback: if still on page after 5 seconds, use window.open
                setTimeout(() => {
                    if (document.getElementById(id)) {
                        this.openCheckoutLinkForSafari(checkoutUrl);
                        // Clean up the form
                        const formElement = document.getElementById(id);
                        if (formElement && formElement.parentNode) {
                            formElement.parentNode.removeChild(formElement);
                        }
                    }
                }, 5000);
            } catch (error) {
                // If form submission fails immediately, fallback to window.open
                this.openCheckoutLinkForSafari(checkoutUrl);
            }
        }

        openCheckoutLinkForSafari(baseUrl: string) {
            const checkoutParams = this.cartManager.getCheckoutParams();
            const urlParams = new URLSearchParams();
            
            checkoutParams.forEach((param) => {
                urlParams.append(param.key, param.value);
            });

            const fullUrl = `${baseUrl}?${urlParams.toString()}`;
            
            // Store URL for fallback message
            this.safariCheckoutUrl = fullUrl;
            this.showSafariFallbackMessage = true;
            
            // Open in new window/tab for Safari
            const target = this.dmnOptions.checkoutMode === 'blank' || window.self !== window.top ? '_blank' : '_self';
            window.open(fullUrl, target);
        }

        closeCheckoutModal() {
            // remove modal
            let container:any = window.document.getElementById(this.checkoutConfig.modalContainerId);
            container.parentNode.removeChild(container);

            this.showCheckout = false;
            this.cartManager.resetCart();

            this.$router.go(-1);
        }

        checkoutInitiatedFromMobile() {
            var mql = window.matchMedia("(max-width: 768px)");
            return mql.matches;
        }

        isSafariBrowser() {
            const userAgent = navigator.userAgent;
            return /^((?!chrome|android).)*safari/i.test(userAgent);
        }
    }
</script>
